"use client";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCheck, Database, FileText, Mail, Zap, Users, Code, X } from "lucide-react";
import { motion } from "motion/react";
import { useState, useRef } from "react";
import { startTransition } from "react";
import { useLocalSession } from "@/hooks/useLocalSession";
import { useNavigate } from "react-router-dom";
import NumberFlow from "@number-flow/react";

const plans = [
  {
    name: "Starter",
    description:
      "Perfect for individuals and small businesses getting started with bank statement conversion",
    price: 9,
    yearlyPrice: 90, // 2 months free
    buttonText: "Get started",
    buttonVariant: "outline" as const,
    features: [
      { text: "Up to 150 pages / month", icon: <FileText size={20} /> },
      { text: "~5–8 statements depending on length", icon: <Database size={20} /> },
      { text: "Email support", icon: <Mail size={20} /> },
    ],
    includes: [
      "Starter includes:",
      "Effective price ≈ $0.06/page",
      "Basic document processing",
      "Standard processing time",
    ],
  },
  {
    name: "Pro",
    description:
      "Best value for growing businesses that need more pages and advanced features",
    price: 24,
    yearlyPrice: 240, // 2 months free
    buttonText: "Get started",
    buttonVariant: "default" as const,
    popular: true,
    features: [
      { text: "Up to 600 pages / month", icon: <FileText size={20} /> },
      { text: "Saved templates / column mapping", icon: <Database size={20} /> },
      { text: "Priority processing", icon: <Zap size={20} /> },
    ],
    includes: [
      "Everything in Starter, plus:",
      "Effective price ≈ $0.04/page",
      "Advanced formatting options",
      "Priority email support",
    ],
  },
  {
    name: "Business",
    description:
      "Advanced plan for teams and businesses needing high volume and integrations",
    price: 59,
    yearlyPrice: 590, // 2 months free
    buttonText: "Get started",
    buttonVariant: "outline" as const,
    features: [
      { text: "Up to 2,500 pages / month", icon: <FileText size={20} /> },
      { text: "Team access", icon: <Users size={20} /> },
      { text: "Custom export formats (Xero/QuickBooks-friendly)", icon: <Database size={20} /> },
      { text: "API/webhooks", icon: <Code size={20} /> },
    ],
    includes: [
      "Everything in Pro, plus:",
      "Effective price ≈ $0.02–0.025/page",
      "Dedicated account support",
      "Custom integrations",
    ],
  },
];

const PricingSwitch = ({ onSwitch }: { onSwitch: (value: string) => void }) => {
  const [selected, setSelected] = useState("0");

  const handleSwitch = (value: string) => {
    setSelected(value);
    onSwitch(value);
  };

  return (
    <div className="flex justify-center">
      <div className="relative z-50 mx-auto flex w-fit rounded-full bg-neutral-50 border border-gray-200 p-1">
        <button
          type="button"
          onClick={() => handleSwitch("0")}
          className={`relative z-10 w-fit sm:h-12 h-10 rounded-full sm:px-6 px-3 sm:py-2 py-1 font-medium transition-colors ${
            selected === "0"
              ? "text-white"
              : "text-muted-foreground hover:text-black"
          }`}
        >
          {selected === "0" && (
            <motion.span
              layoutId={"switch"}
              className="absolute top-0 left-0 sm:h-12 h-10 w-full rounded-full border-4 shadow-sm shadow-primary border-primary bg-gradient-to-t from-primary via-primary/90 to-primary"
              transition={{ type: "spring", stiffness: 500, damping: 30 }}
            />
          )}
          <span className="relative">Monthly</span>
        </button>

        <button
          type="button"
          onClick={() => handleSwitch("1")}
          className={`relative z-10 w-fit sm:h-12 h-8 flex-shrink-0 rounded-full sm:px-6 px-3 sm:py-2 py-1 font-medium transition-colors ${
            selected === "1"
              ? "text-white"
              : "text-muted-foreground hover:text-black"
          }`}
        >
          {selected === "1" && (
            <motion.span
              layoutId={"switch"}
              className="absolute top-0 left-0 sm:h-12 h-10 w-full rounded-full border-4 shadow-sm shadow-primary border-primary bg-gradient-to-t from-primary via-primary/90 to-primary"
              transition={{ type: "spring", stiffness: 500, damping: 30 }}
            />
          )}
          <span className="relative flex items-center gap-2">
            Yearly
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-foreground">
              Save 20%
            </span>
          </span>
        </button>
      </div>
    </div>
  );
};

export default function PricingSection() {
  const isLoggedIn = useLocalSession();
  const navigate = useNavigate();
  const [isYearly, setIsYearly] = useState(false);
  const [isLoading] = useState<string | null>(null);
  const pricingRef = useRef<HTMLDivElement>(null);

  // Marketing page always shows all plans — tier filtering happens in dashboard/pricing
  const filteredPlans = plans;

  const revealVariants = {
    visible: (i: number) => ({
      y: 0,
      opacity: 1,
      filter: "blur(0px)",
      transition: {
        delay: i * 0.1,
        duration: 0.5,
      },
    }),
    hidden: {
      filter: "blur(10px)",
      y: -20,
      opacity: 0,
    },
  };

  const togglePricingPeriod = (value: string) =>
    setIsYearly(Number.parseInt(value) === 1);

  const handleGetStarted = (planName: string) => {
    if (!isLoggedIn) {
      const currentUrl = window.location.pathname + window.location.search;
      startTransition(() => navigate(`/signin?returnUrl=${encodeURIComponent(currentUrl)}`));
      return;
    }
    // Logged-in users go to dashboard/pricing where full auth context is available
    startTransition(() => navigate('/dashboard/pricing'));
  };

  return (
    <div className="px-4 sm:px-6 pt-12 sm:pt-16 md:pt-20 min-h-screen mx-auto relative bg-gradient-subtle" ref={pricingRef}>
      <div
        className="absolute top-0 left-[10%] right-[10%] w-[80%] h-full z-0"
        style={{
          backgroundImage: `
        radial-gradient(circle at center, hsl(var(--primary)) 0%, transparent 70%)
      `,
          opacity: 0.1,
          mixBlendMode: "multiply",
        }}
      />

      <div className="text-center mb-6 sm:mb-8 max-w-3xl mx-auto relative z-10 px-2">
        <motion.h2
          initial="hidden"
          animate="visible"
          variants={revealVariants}
          custom={0}
          className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-medium text-foreground mb-3 sm:mb-4"
        >
          Plans that works best for your{" "}
          <motion.span
            initial="hidden"
            animate="visible"
            variants={revealVariants}
            custom={1}
            className="border border-dashed border-primary px-2 py-1 rounded-xl bg-primary/10 capitalize inline-block text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl"
          >
            business
          </motion.span>
        </motion.h2>

        <motion.p
          initial="hidden"
          animate="visible"
          variants={revealVariants}
          custom={2}
          className="text-xs sm:text-sm md:text-base text-muted-foreground sm:w-[70%] w-[90%] mx-auto px-2"
        >
          Simple pricing. Pay for what you process. No hidden fees, cancel anytime.
        </motion.p>
      </div>

      <motion.div
        initial="hidden"
        animate="visible"
        variants={revealVariants}
        custom={3}
        className="relative z-10"
      >
        <PricingSwitch onSwitch={togglePricingPeriod} />
      </motion.div>

      {filteredPlans.length === 0 ? (
        <div className="text-center py-12 max-w-2xl mx-auto relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={revealVariants}
            custom={4}
            className="bg-card border border-primary/20 rounded-lg p-8"
          >
            <h3 className="text-2xl font-semibold text-foreground mb-2">
              You're on the highest plan!
            </h3>
            <p className="text-muted-foreground">
              You're currently subscribed to the Business plan, which includes all available features.
              If you need custom solutions or enterprise features, please contact our support team.
            </p>
          </motion.div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 max-w-7xl gap-4 sm:gap-6 py-4 sm:py-6 mx-auto relative z-10">
          {/* Free plan card */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={revealVariants}
            custom={4}
            className="h-full flex"
          >
            <Card className="relative border-border flex flex-col h-full w-full bg-card">
              <CardHeader className="text-left flex-shrink-0">
                <div className="flex justify-between">
                  <h3 className="text-3xl font-semibold text-foreground mb-2">Free</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4 h-[4.5rem] overflow-hidden">
                  No credit card required. Try basic bank statement conversion to get started.
                </p>
                <div className="flex items-baseline">
                  <span className="text-4xl font-semibold text-foreground">$0</span>
                  <span className="text-muted-foreground ml-1">/month</span>
                </div>
              </CardHeader>

              <CardContent className="pt-0 flex flex-col flex-1">
                <Button
                  variant="outline"
                  onClick={() => navigate("/signup")}
                  className="w-full mt-3 mb-6 p-4 text-xl rounded-xl flex-shrink-0 bg-gradient-to-t from-foreground to-foreground/90 shadow-lg shadow-foreground/20 border border-border text-primary-foreground hover:bg-foreground"
                >
                  Start Free
                </Button>

                <ul className="space-y-2 font-semibold py-5 flex-shrink-0">
                  <li className="flex items-center">
                    <span className="text-foreground grid place-content-center mt-0.5 mr-3">
                      <FileText size={20} />
                    </span>
                    <span className="text-sm text-muted-foreground">5 pages / week</span>
                  </li>
                </ul>

                <div className="space-y-3 pt-4 border-t border-border mt-auto">
                  <h4 className="font-medium text-base text-foreground mb-3">Free includes:</h4>
                  <ul className="space-y-2 font-semibold">
                    <li className="flex items-center">
                      <span className="h-6 w-6 bg-destructive/10 border border-destructive/30 rounded-full grid place-content-center mt-0.5 mr-3">
                        <X className="h-4 w-4 text-destructive" />
                      </span>
                      <span className="text-sm text-muted-foreground">No validated amounts</span>
                    </li>
                    <li className="flex items-center">
                      <span className="h-6 w-6 bg-destructive/10 border border-destructive/30 rounded-full grid place-content-center mt-0.5 mr-3">
                        <X className="h-4 w-4 text-destructive" />
                      </span>
                      <span className="text-sm text-muted-foreground">No API access</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {filteredPlans.map((plan, index) => (
          <motion.div
            key={plan.name}
            initial="hidden"
            animate="visible"
            variants={revealVariants}
            custom={5 + index}
            className="h-full flex"
          >
            <Card
              className={`relative border-border flex flex-col h-full w-full ${
                plan.popular ? "ring-2 ring-primary bg-primary/5" : "bg-card"
              }`}
            >
              <CardHeader className="text-left flex-shrink-0">
                <div className="flex justify-between">
                  <h3 className="text-3xl font-semibold text-foreground mb-2">
                    {plan.name}
                  </h3>
                  {plan.popular && (
                    <div className="">
                      <span className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium">
                        Popular
                      </span>
                    </div>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-4 h-[4.5rem] overflow-hidden">{plan.description}</p>
                <div className="flex items-baseline">
                  <span className="text-4xl font-semibold text-foreground">
                    $
                    <NumberFlow
                      value={isYearly ? plan.yearlyPrice : plan.price}
                      className="text-4xl font-semibold"
                    />
                  </span>
                  <span className="text-muted-foreground ml-1">
                    /{isYearly ? "year" : "month"}
                  </span>
                </div>
              </CardHeader>

              <CardContent className="pt-0 flex flex-col flex-1">
                <Button
                  variant={plan.buttonVariant}
                  onClick={() => handleGetStarted(plan.name)}
                  disabled={isLoading === plan.name}
                  className={`w-full mb-6 p-4 text-xl rounded-xl flex-shrink-0 ${
                    plan.popular
                      ? "bg-gradient-to-t from-primary to-primary/90 shadow-lg shadow-primary/50 border border-primary/50 text-primary-foreground hover:bg-primary"
                      : plan.buttonVariant === "outline"
                        ? "bg-gradient-to-t from-foreground to-foreground/90 shadow-lg shadow-foreground/20 border border-border text-primary-foreground hover:bg-foreground"
                        : ""
                  }`}
                >
                  {isLoading === plan.name ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    plan.buttonText
                  )}
                </Button>
                <ul className="space-y-2 font-semibold py-5 flex-shrink-0">
                  {plan.features.map((feature) => (
                    <li key={feature.text} className="flex items-center">
                      <span className="text-foreground grid place-content-center mt-0.5 mr-3">
                        {feature.icon}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="space-y-3 pt-4 border-t border-border mt-auto">
                  <h4 className="font-medium text-base text-foreground mb-3">
                    {plan.includes[0]}
                  </h4>
                  <ul className="space-y-2 font-semibold">
                    {plan.includes.slice(1).map((feature) => (
                      <li key={feature} className="flex items-center">
                        <span className="h-6 w-6 bg-primary/10 border border-primary rounded-full grid place-content-center mt-0.5 mr-3">
                          <CheckCheck className="h-4 w-4 text-primary" />
                        </span>
                        <span className="text-sm text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
        </div>
      )}
    </div>
  );
}
